import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { LayoutListRow } from "../LayoutListRow";
import { NO_ZONE_RGB, NO_ZONE_SHAPE_ID } from "../utils/floorPlanLayout";
import { getLayoutShapeId } from "../utils/floorPlanLayout/getLayoutShapeId";
import { layoutHighlightStore } from "../utils/floorPlanLayout/layoutHighlightStore";
import type { ZonesListProps } from "./types";

const highlightOnHover = (shapeId: string) => (isHovered: boolean) =>
  layoutHighlightStore.set(isHovered ? shapeId : null);

export const ZonesList = ({
  zones,
  zoneColorById,
  roomCountByZoneId,
  onSelectZone,
}: ZonesListProps) => {
  const { t } = useTranslation();

  const roomsWithoutZoneCount = roomCountByZoneId.get(null) ?? 0;

  useEffect(() => () => layoutHighlightStore.set(null), []);

  return (
    <div className="flex flex-col gap-3 min-h-0">
      <p className="text-xs text-gray-500">
        {t("smartSurveyor.zones.listHint")}
      </p>
      <ul className="flex flex-col gap-2 overflow-y-auto min-h-0 p-1">
        {zones.map((zone, position) => {
          const roomCount = roomCountByZoneId.get(zone.id) ?? 0;
          return (
            <LayoutListRow
              key={zone.id}
              index={position + 1}
              name={zone.name}
              colorRgb={zoneColorById.get(zone.id)}
              badge={t(`smartSurveyor.zones.kind.${zone.kind}`)}
              detail={t("smartSurveyor.zones.roomsCount", {
                count: roomCount,
              })}
              onClick={roomCount > 0 ? () => onSelectZone(zone.id) : undefined}
              onHoverChange={highlightOnHover(
                getLayoutShapeId({ kind: "zone", id: zone.id }),
              )}
            />
          );
        })}
        {roomsWithoutZoneCount > 0 && (
          <LayoutListRow
            index={zones.length + 1}
            name={t("smartSurveyor.zones.noZone")}
            colorRgb={NO_ZONE_RGB}
            detail={t("smartSurveyor.zones.roomsCount", {
              count: roomsWithoutZoneCount,
            })}
            onHoverChange={highlightOnHover(NO_ZONE_SHAPE_ID)}
          />
        )}
      </ul>
    </div>
  );
};
